import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState, StatusPill } from '@ui-kit';
import type { StatusTone } from '@ui-kit';
import { useSagaInstances } from '../hooks/useSagaInstances';
import { useSagaSummary } from '../hooks/useSagaSummary';
import { useTriggerDemoSaga } from '../hooks/useTriggerDemoSaga';
import { SagaInstanceDetailDrawer } from '../components/SagaInstanceDetailDrawer';
import { CommonUsagePanel } from '../../_shared/CommonUsagePanel';
import { ButtonGuard } from '../../_shared/components/ButtonGuard';
import type { SagaStatus, SagaInstanceFilter, SagaInstanceListItem, ApiError } from '../types/sagaMonitor';

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_FILTERS: SagaInstanceFilter = {
  page: 0,
  size: DEFAULT_PAGE_SIZE,
  sort: 'startedAt,desc',
};

const SAGA_STATUSES: Array<{ value: SagaStatus | ''; labelKey: string }> = [
  { value: '', labelKey: 'sagaMonitor.list.filter.statusAll' },
  { value: 'STARTED', labelKey: 'sagaMonitor.status.STARTED' },
  { value: 'COMPLETED', labelKey: 'sagaMonitor.status.COMPLETED' },
  { value: 'COMPENSATING', labelKey: 'sagaMonitor.status.COMPENSATING' },
  { value: 'COMPENSATED', labelKey: 'sagaMonitor.status.COMPENSATED' },
  { value: 'FAILED', labelKey: 'sagaMonitor.status.FAILED' },
];

const DEMO_SAGA_NAMES = ['order-create'] as const;

function sagaStatusTone(status: SagaStatus): StatusTone {
  switch (status) {
    case 'COMPLETED': return 'success';
    case 'COMPENSATED': return 'info';
    case 'FAILED': return 'danger';
    case 'COMPENSATING': return 'warning';
    case 'STARTED': return 'neutral';
    default: return 'neutral';
  }
}

function formatDateTime(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/** 목록 skeleton 행 */
function SkeletonRow({ cols }: { cols: number }) {
  return (
    <tr>
      {Array.from({ length: cols }).map((_, i) => (
        <td
          key={i}
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderBottom: '1px solid var(--color-surface-border)',
          }}
        >
          <div
            style={{
              height: 12,
              width: `${50 + (i % 3) * 15}%`,
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-elevated)',
              animation: 'pulse 1.5s ease-in-out infinite',
            }}
          />
        </td>
      ))}
    </tr>
  );
}

/** 요약 집계 카드 */
function SummaryCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | undefined;
  tone: StatusTone;
}) {
  const toneColorMap: Record<StatusTone, string> = {
    neutral: 'var(--color-text-secondary)',
    success: 'var(--color-success)',
    info:    'var(--color-info)',
    warning: 'var(--color-warning)',
    danger:  'var(--color-danger)',
  };
  return (
    <div
      style={{
        flex: '1 1 0',
        minWidth: 80,
        background: 'var(--color-surface-background)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        padding: 'var(--spacing-3) var(--spacing-4)',
        textAlign: 'center',
      }}
    >
      <p
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-secondary)',
          margin: '0 0 var(--spacing-1)',
          fontWeight: 500,
        }}
      >
        {label}
      </p>
      <p
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          margin: 0,
          color: toneColorMap[tone],
          fontFamily: 'var(--font-family-mono)',
        }}
      >
        {value ?? '-'}
      </p>
    </div>
  );
}

const COMMON_USAGE_ITEMS = [
  {
    module: 'eraf-data-jpa (C067)',
    moduleColor: 'green' as const,
    api: 'SagaOrchestrator · SagaInstanceRepository',
    role: '데모 사가 실행/재시작 + 인스턴스 조회',
    snippet: 'orchestrator.execute(def, ctx)',
  },
  {
    module: 'eraf-core (C021)',
    moduleColor: 'blue' as const,
    api: '@Idempotent',
    role: '데모 트리거·재시작 중복 요청 방지',
    snippet: '@Idempotent(ttl=30)',
  },
  {
    module: 'eraf-core (C022)',
    moduleColor: 'blue' as const,
    api: '@DistributedLock',
    role: '동일 인스턴스 동시 resume 직렬화',
    snippet: '@DistributedLock(key="saga:#{id}")',
  },
];

/**
 * SagaMonitorListPage — 사가 인스턴스 목록 페이지 (/saga-monitor)
 * - 상태 필터 + DataTable
 * - 요약 집계 카드 (6종 상태)
 * - 데모 사가 트리거 다이얼로그
 * - 상세 드로어 (dialog 상태)
 * - 5상태: loading(skeleton) · empty · error · dialog(drawer/demo) · default
 */
export function SagaMonitorListPage() {
  const { t, i18n } = useTranslation();

  // 필터 상태
  const [pendingFilters, setPendingFilters] = useState<SagaInstanceFilter>({});
  const [appliedFilters, setAppliedFilters] = useState<SagaInstanceFilter>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);

  // 드로어
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);

  // 데모 트리거 다이얼로그
  const [demoDialogOpen, setDemoDialogOpen] = useState(false);
  const [demoSagaName, setDemoSagaName] = useState<string>(DEMO_SAGA_NAMES[0]);
  const [demoFailAtStep, setDemoFailAtStep] = useState<string>('');

  // 토스트
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }, []);

  // 데이터 쿼리
  const listQuery = useSagaInstances(appliedFilters);
  const summaryQuery = useSagaSummary();
  const triggerDemoMutation = useTriggerDemoSaga();

  const pageData = listQuery.data;

  // 필터 핸들러
  const handleFilterChange = useCallback(
    (partial: Partial<SagaInstanceFilter>) => {
      setPendingFilters((prev) => ({ ...prev, ...partial }));
    },
    []
  );

  const handleSearch = useCallback(() => {
    const next: SagaInstanceFilter = {
      ...pendingFilters,
      page: 0,
      size: DEFAULT_PAGE_SIZE,
      sort: 'startedAt,desc',
    };
    Object.keys(next).forEach((k) => {
      const key = k as keyof SagaInstanceFilter;
      if (next[key] === '' || next[key] === undefined) {
        delete next[key];
      }
    });
    setPage(0);
    setAppliedFilters({ ...next, page: 0, size: DEFAULT_PAGE_SIZE, sort: 'startedAt,desc' });
  }, [pendingFilters]);

  const handleReset = useCallback(() => {
    setPendingFilters({});
    setPage(0);
    setAppliedFilters(DEFAULT_FILTERS);
  }, []);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
    setAppliedFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleRowClick = useCallback((row: SagaInstanceListItem) => {
    setSelectedInstanceId(row.id);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setSelectedInstanceId(null);
  }, []);

  // 데모 트리거
  const handleDemoSubmit = useCallback(() => {
    triggerDemoMutation.mutate(
      {
        sagaName: demoSagaName,
        body: { failAtStep: demoFailAtStep || null },
      },
      {
        onSuccess: (data) => {
          setDemoDialogOpen(false);
          setDemoFailAtStep('');
          showToast(
            t('sagaMonitor.demo.success', {
              id: String(data.instanceId).substring(0, 8) + '...',
              status: t(`sagaMonitor.status.${data.status}`),
            })
          );
        },
        onError: () => {
          setDemoDialogOpen(false);
          showToast(t('sagaMonitor.demo.error'));
        },
      }
    );
  }, [demoSagaName, demoFailAtStep, triggerDemoMutation, showToast, t]);

  // 테이블 헤더
  const TABLE_HEADERS = useMemo(() => [
    t('sagaMonitor.list.column.sagaName'),
    t('sagaMonitor.list.column.status'),
    t('sagaMonitor.list.column.startedAt'),
    t('sagaMonitor.list.column.failedStep'),
    t('sagaMonitor.list.column.completedAt'),
  ], [t]);

  const isLoading = listQuery.isLoading;
  const isError = listQuery.isError;
  const isEmpty = !isLoading && !isError && (pageData?.empty ?? false);
  const showTable = !isLoading && !isError && !isEmpty && !!pageData;

  const summary = summaryQuery.data;

  return (
    <div style={{ padding: 0 }}>
      {/* 페이지 헤더 */}
      <PageHeader
        title={t('sagaMonitor.list.title')}
        breadcrumb={<span>{t('sagaMonitor.list.breadcrumb')}</span>}
        actions={
          <ButtonGuard requirePermission="SAGA_MONITOR_TRIGGER">
            {({ disabled, reason }) => (
              <button
                type="button"
                disabled={disabled}
                title={reason}
                onClick={() => setDemoDialogOpen(true)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 'var(--spacing-2)',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  background: disabled ? 'var(--color-surface-muted)' : 'var(--color-brand-secondary)',
                  color: disabled ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  fontFamily: 'var(--font-family-sans)',
                  opacity: disabled ? 0.6 : 1,
                }}
              >
                ▶ {t('sagaMonitor.demo.button')}
              </button>
            )}
          </ButtonGuard>
        }
      />

      {/* 요약 집계 카드 */}
      <div
        aria-label={t('sagaMonitor.summary.loadingAriaLabel')}
        aria-busy={summaryQuery.isLoading}
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
          margin: 'var(--spacing-4) 0',
        }}
      >
        <SummaryCard label={t('sagaMonitor.summary.total')}       value={summary?.total}       tone="neutral"  />
        <SummaryCard label={t('sagaMonitor.summary.started')}     value={summary?.started}     tone="neutral"  />
        <SummaryCard label={t('sagaMonitor.summary.completed')}   value={summary?.completed}   tone="success"  />
        <SummaryCard label={t('sagaMonitor.summary.compensating')}value={summary?.compensating}tone="warning"  />
        <SummaryCard label={t('sagaMonitor.summary.compensated')} value={summary?.compensated} tone="info"     />
        <SummaryCard label={t('sagaMonitor.summary.failed')}      value={summary?.failed}      tone="danger"   />
      </div>

      {/* 필터 바 */}
      <div
        role="search"
        aria-label={t('sagaMonitor.list.filter.sagaName')}
        style={{
          display: 'flex',
          gap: 'var(--spacing-3)',
          flexWrap: 'wrap',
          alignItems: 'flex-end',
          padding: 'var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        {/* 사가명 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '1 1 200px' }}>
          <label
            htmlFor="filter-sagaName"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('sagaMonitor.list.filter.sagaName')}
          </label>
          <input
            id="filter-sagaName"
            type="text"
            value={pendingFilters.sagaName ?? ''}
            onChange={(e) => handleFilterChange({ sagaName: e.target.value })}
            placeholder={t('sagaMonitor.list.filter.sagaNamePlaceholder')}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSearch(); }}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
            }}
          />
        </div>

        {/* 상태 필터 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)', flex: '0 0 160px' }}>
          <label
            htmlFor="filter-status"
            style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)' }}
          >
            {t('sagaMonitor.list.filter.status')}
          </label>
          <select
            id="filter-status"
            value={pendingFilters.status ?? ''}
            onChange={(e) => handleFilterChange({ status: e.target.value as SagaStatus | '' })}
            style={{
              padding: 'var(--spacing-2) var(--spacing-3)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {SAGA_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {t(s.labelKey)}
              </option>
            ))}
          </select>
        </div>

        {/* 검색/초기화 버튼 */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', alignItems: 'flex-end' }}>
          <button
            type="button"
            onClick={handleSearch}
            disabled={isLoading}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.6 : 1,
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('sagaMonitor.list.filter.button.search')}
          </button>
          <button
            type="button"
            onClick={handleReset}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              color: 'var(--color-text-secondary)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('sagaMonitor.list.filter.button.reset')}
          </button>
        </div>
      </div>

      {/* 결과 수 */}
      {pageData && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: '0 0 var(--spacing-3)',
          }}
        >
          {pageData.totalElements > 0
            ? t('sagaMonitor.list.totalCount', { count: pageData.totalElements })
            : t('sagaMonitor.list.noResults')}
        </p>
      )}

      {/* 컨텐츠 영역 — 5상태 */}
      <div
        style={{
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          background: 'var(--color-surface-background)',
        }}
      >
        {/* Loading — skeleton */}
        {isLoading && (
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
            aria-label={t('sagaMonitor.list.loadingAriaLabel')}
            aria-busy="true"
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {TABLE_HEADERS.map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-3) var(--spacing-4)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 5 }).map((_, i) => (
                <SkeletonRow key={i} cols={TABLE_HEADERS.length} />
              ))}
            </tbody>
          </table>
        )}

        {/* Error */}
        {isError && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <ErrorState
              title={t('sagaMonitor.list.error.loadFailed')}
              message={
                (listQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                  ? t('sagaMonitor.list.error.forbidden')
                  : ((listQuery.error as unknown as ApiError)?.message ??
                    t('sagaMonitor.error.network'))
              }
              onRetry={() => void listQuery.refetch()}
            />
          </div>
        )}

        {/* Empty */}
        {isEmpty && (
          <div style={{ padding: 'var(--spacing-6)' }}>
            <EmptyState
              title={t('sagaMonitor.list.empty.title')}
              description={t('sagaMonitor.list.empty.description')}
              action={
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    background: 'var(--color-brand-primary)',
                    color: 'var(--color-text-inverse)',
                    border: 'none',
                    borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-sans)',
                  }}
                >
                  {t('sagaMonitor.list.empty.resetButton')}
                </button>
              }
            />
          </div>
        )}

        {/* Default — 테이블 */}
        {showTable && pageData && (
          <>
            <div role="region" aria-label={t('sagaMonitor.list.tableAriaLabel')} style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
                aria-label={t('sagaMonitor.list.tableAriaLabel')}
              >
                <thead>
                  <tr style={{ background: 'var(--color-surface-muted)' }}>
                    {TABLE_HEADERS.map((h) => (
                      <th
                        key={h}
                        style={{
                          textAlign: 'left',
                          padding: 'var(--spacing-3) var(--spacing-4)',
                          borderBottom: '1px solid var(--color-surface-border)',
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-secondary)',
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pageData.content.map((row) => (
                    <tr
                      key={row.id}
                      tabIndex={0}
                      role="button"
                      aria-label={t('sagaMonitor.list.rowAriaLabel', { id: row.id })}
                      onClick={() => handleRowClick(row)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleRowClick(row);
                        }
                      }}
                      style={{ cursor: 'pointer', transition: 'background 0.1s' }}
                      onMouseEnter={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background =
                          'var(--color-surface-muted)';
                      }}
                      onMouseLeave={(e) => {
                        (e.currentTarget as HTMLTableRowElement).style.background = '';
                      }}
                    >
                      {/* 사가명 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-primary)' }}>
                          {row.sagaName}
                        </span>
                      </td>
                      {/* 상태 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <StatusPill tone={sagaStatusTone(row.status)}>
                          {t(`sagaMonitor.status.${row.status}`)}
                        </StatusPill>
                      </td>
                      {/* 시작 시각 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                          {formatDateTime(row.startedAt, i18n.language)}
                        </span>
                      </td>
                      {/* 실패 단계 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        {row.failedStep ? (
                          <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', fontWeight: 600 }}>
                            {row.failedStep}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)' }}>-</span>
                        )}
                      </td>
                      {/* 종료 시각 */}
                      <td style={{ padding: 'var(--spacing-3) var(--spacing-4)', borderBottom: '1px solid var(--color-surface-border)' }}>
                        <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                          {row.finishedAt ? formatDateTime(row.finishedAt, i18n.language) : '-'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 페이지네이션 */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'var(--color-surface-muted)',
                borderTop: '1px solid var(--color-surface-border)',
                flexWrap: 'wrap',
                gap: 'var(--spacing-2)',
              }}
            >
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>
                {t('sagaMonitor.list.pagination.showing', {
                  total: pageData.totalElements.toLocaleString(),
                  from: pageData.number * pageData.size + 1,
                  to: Math.min((pageData.number + 1) * pageData.size, pageData.totalElements),
                })}
              </span>
              <nav aria-label={t('sagaMonitor.list.pagination.nav')} style={{ display: 'inline-flex', gap: 'var(--spacing-1)' }}>
                <button
                  type="button"
                  disabled={pageData.first}
                  onClick={() => handlePageChange(page - 1)}
                  aria-label={t('sagaMonitor.list.pagination.prev')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.first ? 'not-allowed' : 'pointer',
                    opacity: pageData.first ? 0.5 : 1,
                  }}
                >
                  &lt;
                </button>
                {Array.from({ length: Math.min(pageData.totalPages, 10) }).map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handlePageChange(i)}
                    aria-label={t('sagaMonitor.list.pagination.page', { page: i + 1 })}
                    aria-current={i === pageData.number ? 'page' : undefined}
                    style={{
                      minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                      borderRadius: 'var(--radius-sm)',
                      background: i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-elevated)',
                      border: `1px solid ${i === pageData.number ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
                      color: i === pageData.number ? 'var(--color-text-inverse)' : 'var(--color-text-secondary)',
                      fontSize: 'var(--font-size-xs)',
                      cursor: 'pointer',
                      fontWeight: i === pageData.number ? 600 : 400,
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={pageData.last}
                  onClick={() => handlePageChange(page + 1)}
                  aria-label={t('sagaMonitor.list.pagination.next')}
                  style={{
                    minWidth: 28, height: 28, padding: '0 var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-surface-elevated)',
                    border: '1px solid var(--color-surface-border)',
                    fontSize: 'var(--font-size-xs)',
                    cursor: pageData.last ? 'not-allowed' : 'pointer',
                    opacity: pageData.last ? 0.5 : 1,
                  }}
                >
                  &gt;
                </button>
              </nav>
            </div>
          </>
        )}
      </div>

      {/* CommonUsagePanel — §6.8 */}
      <CommonUsagePanel
        items={COMMON_USAGE_ITEMS}
        title={t('sagaMonitor.commonUsage.title')}
        subtitle={t('sagaMonitor.commonUsage.subtitle')}
      />

      {/* 드로어 — dialog 상태 */}
      {selectedInstanceId !== null && (
        <SagaInstanceDetailDrawer
          instanceId={selectedInstanceId}
          onClose={handleDrawerClose}
          onResumed={(msg) => {
            handleDrawerClose();
            showToast(msg);
          }}
        />
      )}

      {/* 데모 트리거 다이얼로그 */}
      {demoDialogOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="demo-dialog-title"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              width: 440,
              maxWidth: '90vw',
              boxShadow: 'var(--shadow-xl)',
            }}
          >
            <h2
              id="demo-dialog-title"
              style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, marginBottom: 'var(--spacing-2)' }}
            >
              {t('sagaMonitor.demo.dialog.title')}
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-5)',
                lineHeight: 1.6,
              }}
            >
              {t('sagaMonitor.demo.dialog.description')}
            </p>

            {/* 사가 유형 선택 */}
            <div style={{ marginBottom: 'var(--spacing-4)' }}>
              <label
                htmlFor="demo-saga-name"
                style={{ display: 'block', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
              >
                {t('sagaMonitor.demo.dialog.sagaName.label')}
              </label>
              <select
                id="demo-saga-name"
                value={demoSagaName}
                onChange={(e) => setDemoSagaName(e.target.value)}
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                  boxSizing: 'border-box',
                }}
              >
                {DEMO_SAGA_NAMES.map((name) => (
                  <option key={name} value={name}>
                    {t('sagaMonitor.demo.dialog.sagaName.orderCreate')}
                  </option>
                ))}
              </select>
            </div>

            {/* 실패 주입 단계 선택 */}
            <div style={{ marginBottom: 'var(--spacing-5)' }}>
              <label
                htmlFor="demo-fail-at-step"
                style={{ display: 'block', fontSize: 'var(--font-size-xs)', fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}
              >
                {t('sagaMonitor.demo.dialog.failAtStep.label')}
              </label>
              <select
                id="demo-fail-at-step"
                value={demoFailAtStep}
                onChange={(e) => setDemoFailAtStep(e.target.value)}
                aria-describedby="demo-fail-hint"
                style={{
                  width: '100%',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  background: 'var(--color-surface-elevated)',
                  color: 'var(--color-text-primary)',
                  fontFamily: 'var(--font-family-sans)',
                  boxSizing: 'border-box',
                }}
              >
                <option value="">{t('sagaMonitor.demo.dialog.failAtStep.none')}</option>
                <option value="reserve-stock">{t('sagaMonitor.demo.dialog.failAtStep.reserveStock')}</option>
                <option value="charge-payment">{t('sagaMonitor.demo.dialog.failAtStep.chargePayment')}</option>
                <option value="book-delivery">{t('sagaMonitor.demo.dialog.failAtStep.bookDelivery')}</option>
              </select>
              <p
                id="demo-fail-hint"
                style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)', margin: 'var(--spacing-1) 0 0' }}
              >
                {demoFailAtStep
                  ? `선택 단계에서 실패를 주입하여 보상 트랜잭션 경로를 시연합니다.`
                  : `성공 경로로 실행합니다. 3단계 전체가 DONE 상태가 됩니다.`}
              </p>
            </div>

            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => { setDemoDialogOpen(false); setDemoFailAtStep(''); }}
                disabled={triggerDemoMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  cursor: 'pointer',
                }}
              >
                {t('sagaMonitor.demo.dialog.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDemoSubmit}
                disabled={triggerDemoMutation.isPending}
                style={{
                  padding: 'var(--spacing-2) var(--spacing-4)',
                  background: triggerDemoMutation.isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                  color: triggerDemoMutation.isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                  border: 'none',
                  borderRadius: 'var(--radius-md)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 600,
                  cursor: triggerDemoMutation.isPending ? 'not-allowed' : 'pointer',
                  opacity: triggerDemoMutation.isPending ? 0.6 : 1,
                }}
              >
                {triggerDemoMutation.isPending ? '...' : t('sagaMonitor.demo.dialog.submit')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 */}
      {toast !== null && (
        <div
          role="alert"
          style={{
            position: 'fixed',
            bottom: 'var(--spacing-6)',
            right: 'var(--spacing-6)',
            zIndex: 1100,
            background: 'var(--color-surface-overlay, #1e1e2e)',
            color: 'var(--color-text-inverse)',
            padding: 'var(--spacing-3) var(--spacing-5)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            boxShadow: 'var(--shadow-lg)',
            maxWidth: 380,
          }}
        >
          {toast}
        </div>
      )}

      {/* 반응형 스타일 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 768px) {
          table { min-width: 560px; }
        }
      `}</style>
    </div>
  );
}
